import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, List, ListItemButton, ListItemText, Collapse } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";
import { getLangFromCookie, LANG_COOKIE } from "../../../Utils/LangCookie";

/* ------------------------------- Lang helpers ------------------------------- */
const getCurrentLang = () => getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";
const labelByLang = (item, lang) =>
  lang === "EN" ? item.EN || item.label : lang === "JP" ? item.JP || item.label : item.label;

/* ------------------------------- Path helpers ------------------------------- */
export const ensureLeadingSlash = (p) => (p?.startsWith("/") ? p : `/${p}`);
export const normalizeHref = (p) => ensureLeadingSlash(String(p || "")).replace(/\/+$/, "");

const tailKey = (path = "") =>
  decodeURI(String(path).trim())
    .replace(/^\/+/, "")
    .replace(/^b2b\//, "")
    .replace(/^b2c\//, "")
    .replace(/^(商品資訊|商品分類|全站商品|合作資訊)\//, "")
    .replace(/\/{2,}/g, "/")
    .trim();

export function findNodeByHref(nodes, href) {
  if (!Array.isArray(nodes)) return null;
  const target = normalizeHref(href);
  for (const node of nodes) {
    if (normalizeHref(node.href) === target) return node;
    const found = findNodeByHref(node.children, href);
    if (found) return found;
  }
  return null;
}
export function isAncestorByTail(nodeHref, currentHref) {
  const a = tailKey(nodeHref);
  const b = tailKey(currentHref);
  if (!a) return false;
  if (a === b) return true;
  return b.startsWith(a + "/");
}
export function findProductInfoRoot(config = []) {
  if (!Array.isArray(config)) return null;
  return config[0] || null;
}
export function collectHrefSet(node) {
  const set = new Set();
  (function walk(n) {
    if (!n) return;
    if (n.href) set.add(normalizeHref(n.href));
    if (Array.isArray(n.children)) n.children.forEach(walk);
  })(node);
  return set;
}
export function getAncestors(root, target) {
  if (!root || !target) return [];
  const path = [];
  function dfs(n) {
    path.push(n);
    if (n === target) return true;
    if (Array.isArray(n.children)) {
      for (const c of n.children) if (dfs(c)) return true;
    }
    path.pop();
    return false;
  }
  dfs(root);
  return path;
}

const nodeLocked = (node, ancestorLocked, isLoggedIn) =>
  !isLoggedIn && (ancestorLocked || node?.login === "required");

/* --------------------------------- Nodes ---------------------------------- */
function SidebarNode({
  node,
  currentHref,
  level = 0,
  onSelect,
  isLoggedIn,
  onRequireLogin,
  ancestorLocked = false,
  isOpen = false,
  onToggleRequested,
  lang = "ZH",
}) {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const selected = tailKey(node.href) === tailKey(currentHref);
  const locked = nodeLocked(node, ancestorLocked, isLoggedIn);
  const rowRef = useRef(null);

  useEffect(() => {
    if (!selected) return;
    const t = setTimeout(() => {
      try {
        rowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
        rowRef.current?.focus({ preventScroll: true });
      } catch {}
    }, 60);
    return () => clearTimeout(t);
  }, [selected]);

  const handleRowClick = () => {
    if (locked) return onRequireLogin?.(node.href);
    onSelect?.(node);
    if (hasChildren && !isOpen) onToggleRequested?.(node);
  };

  const handleCaretClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (locked) return onRequireLogin?.(node.href);
    if (hasChildren) onToggleRequested?.(node);
  };

  return (
    <Box key={node.href}>
      <ListItemButton
        ref={rowRef}
        onClick={handleRowClick}
        disableRipple
        sx={{
          pl: 2 + level * 2,
          pr: 1.25,
          py: 0.75,
          minHeight: 36,
          opacity: locked ? 0.55 : 1,
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
          justifyContent: "flex-start",
          outline: selected ? (t) => `2px solid ${t.palette.primary.main}` : "none",
          outlineOffset: 2,
        }}
        selected={selected}
        tabIndex={selected ? 0 : -1}
      >
        <ListItemText
          primary={labelByLang(node, lang)}
          primaryTypographyProps={{ fontSize: 16, lineHeight: 1.4, sx: { userSelect: "none" } }}
          sx={{ mr: 0, flex: "0 1 auto" }}
        />
        {hasChildren && (
          <Box
            component="span"
            onClick={handleCaretClick}
            sx={{
              ml: 0.5,
              color: "text.secondary",
              fontSize: 14,
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 160ms ease",
              flex: "0 0 auto",
              cursor: "pointer",
            }}
            role="button"
            aria-label={isOpen ? "收合" : "展開"}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCaretClick(e);
              }
            }}
          >
            ∨
          </Box>
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <SidebarTree
            nodes={node.children}
            currentHref={currentHref}
            level={level + 1}
            onSelect={onSelect}
            isLoggedIn={isLoggedIn}
            onRequireLogin={onRequireLogin}
            ancestorLocked={ancestorLocked || node.login === "required"}
            lang={lang}
          />
        </Collapse>
      )}
    </Box>
  );
}

function SidebarTree({
  nodes,
  currentHref,
  level = 0,
  onSelect,
  isLoggedIn,
  onRequireLogin,
  ancestorLocked = false,
  lang = "ZH",
}) {
  const isEmpty = !Array.isArray(nodes) || nodes.length === 0;

  const defaultOpenHref = useMemo(() => {
    if (isEmpty) return null;
    const n = nodes.find((n) => isAncestorByTail(n.href, currentHref));
    return n?.href || null;
  }, [isEmpty, nodes, currentHref]);

  const [openHref, setOpenHref] = useState(defaultOpenHref);
  useEffect(() => {
    setOpenHref((prev) => (prev == null ? defaultOpenHref : prev));
  }, [defaultOpenHref]);

  const handleToggleRequested = (node) => {
    setOpenHref((prev) => (prev === node.href ? null : node.href));
  };

  if (isEmpty) return null;

  return (
    <List disablePadding>
      {nodes.map((node) => (
        <SidebarNode
          key={node.href}
          node={node}
          currentHref={currentHref}
          level={level}
          onSelect={onSelect}
          isLoggedIn={isLoggedIn}
          onRequireLogin={onRequireLogin}
          ancestorLocked={ancestorLocked}
          isOpen={openHref === node.href}
          onToggleRequested={handleToggleRequested}
          lang={lang}
        />
      ))}
    </List>
  );
}

/* ----------------------------- Public widget ----------------------------- */
/**
 * If `mobile` is true, this renders in a Drawer (no sticky/viewport height).
 * If `mobile` is false (default), it renders a sticky left rail.
 */
export default function ProductSidebar({ rootNode, currentHref, onSelect, onClose, mobile = false }) {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  const navigate = useNavigate();
  const location = useLocation();
  const modePrefix = location.pathname.startsWith("/b2c")
    ? "/b2c"
    : location.pathname.startsWith("/")
    ? "/"
    : "/b2c";

  const [lang, setLang] = useState(getCurrentLang());
  useEffect(() => {
    const onChange = () => setLang(getCurrentLang());
    window.addEventListener("app:languageChanged", onChange);
    return () => window.removeEventListener("app:languageChanged", onChange);
  }, []);

  const children = useMemo(() => rootNode?.children ?? [], [rootNode]);

  const displayNodes = useMemo(() => {
    const head = rootNode
      ? [
          {
            ...rootNode,
            label: labelByLang(rootNode, lang),
            href: normalizeHref(rootNode.href),
            children: undefined,
          },
        ]
      : [
          {
            label: lang === "EN" ? "Product Information" : lang === "JP" ? "製品情報" : "商品資訊",
            href: `${modePrefix}/商品資訊`,
          },
        ];
    return [...head, ...children];
  }, [children, rootNode, modePrefix, lang]);

  const onRequireLogin = (href) => {
    const target = normalizeHref(href || `${modePrefix}/`);
    navigate(`${modePrefix}/auth`, { state: { redirectTo: target } });
  };

  return (
    <Box
      sx={{
        width: mobile ? "100%" : { xs: 0, md: 220 },
        minWidth: mobile ? "100%" : { xs: 0, md: 220 },
        flex: mobile ? "1 1 auto" : { xs: "0 0 0", md: "0 0 220px" },
        p: mobile ? 1.5 : 1.5,
        position: mobile ? "relative" : "sticky",
        top: mobile ? "auto" : 0,
        alignSelf: mobile ? "auto" : "flex-start",
        height: mobile ? "auto" : "100dvh",
        overflow: "auto",
        bgcolor: "background.paper",
      }}
      role="navigation"
      aria-label="商品分類側邊欄"
    >
      <SidebarTree
        nodes={displayNodes}
        currentHref={currentHref}
        onSelect={(node) => {
          onSelect?.(node);
          // If used inside Drawer, close after selecting
          onClose?.();
        }}
        isLoggedIn={isLoggedIn}
        onRequireLogin={onRequireLogin}
        ancestorLocked={rootNode?.login === "required"}
        lang={lang}
      />
    </Box>
  );
}
